/**
 * useTimeTracking.js
 *
 * Hook for managing Operations Analyst clock in/out functionality.
 * Includes idle timeout detection and shift management.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  getDocs,
  limit,
} from "firebase/firestore";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const PAY_RATE = 20; // $20/hour
const LOGOUT_GRACE_MS = 2 * 60 * 1000; // 2 minutes grace period after logout

/**
 * Mark an agent's active shift with a logoutAt timestamp.
 * Called before signOut so the shift can be resumed within the grace period.
 * @param {string} agentId - User ID of the agent
 */
export async function markAgentLogout(agentId) {
  if (!agentId) return;
  try {
    const q = query(
      collection(db, "timeLogs"),
      where("agentId", "==", agentId),
      where("clockOut", "==", null),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const shiftDoc = snapshot.docs[0];
      await updateDoc(doc(db, "timeLogs", shiftDoc.id), {
        logoutAt: Timestamp.now(),
      });
    }
  } catch (err) {
    console.error("markAgentLogout error:", err);
  }
}

/**
 * Hook for agent time tracking
 * @param {string} agentId - User ID of the agent
 * @param {string} displayName - Display name of the agent
 * @returns {Object} Time tracking state and functions
 */
export function useTimeTracking(agentId, displayName) {
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);

  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const elapsedIntervalRef = useRef(null);

  // Check for active shift on mount
  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      return;
    }

    let isCurrent = true;

    const checkActiveShift = async () => {
      try {
        const q = query(
          collection(db, "timeLogs"),
          where("agentId", "==", agentId),
          where("clockOut", "==", null),
          limit(1)
        );

        const snapshot = await getDocs(q);
        if (!isCurrent) return;

        if (!snapshot.empty) {
          const shiftDoc = snapshot.docs[0];
          const shiftData = { id: shiftDoc.id, ...shiftDoc.data() };

          // Grace period check: if logoutAt is set, the agent was logged out mid-shift
          if (shiftData.logoutAt) {
            const logoutTime = shiftData.logoutAt.toDate();
            const elapsed = Date.now() - logoutTime.getTime();

            if (elapsed >= LOGOUT_GRACE_MS) {
              // Grace period expired — auto-clock out at logoutAt + 2 minutes
              const clockOutTime = new Date(logoutTime.getTime() + LOGOUT_GRACE_MS);
              const clockInTime = shiftData.clockIn.toDate();
              const totalMs = clockOutTime.getTime() - clockInTime.getTime();
              const totalHours = Math.round((totalMs / (1000 * 60 * 60)) * 100) / 100;
              const totalPay = Math.round(totalHours * PAY_RATE * 100) / 100;

              await updateDoc(doc(db, "timeLogs", shiftDoc.id), {
                clockOut: Timestamp.fromDate(clockOutTime),
                totalHours,
                totalPay,
                autoClockOut: true,
                graceExpiredClockOut: true,
                logoutAt: null,
                updatedAt: Timestamp.now(),
              });
              // Don't resume — shift is closed
              if (isCurrent) setLoading(false);
              return;
            } else {
              // Within grace period — clear logoutAt and resume shift
              await updateDoc(doc(db, "timeLogs", shiftDoc.id), {
                logoutAt: null,
              });
            }
          }

          setCurrentShift(shiftData);
          setIsClockedIn(true);

          // Calculate elapsed time
          if (shiftData.clockIn) {
            const clockInTime = shiftData.clockIn.toDate().getTime();
            setElapsedTime(Date.now() - clockInTime);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error("Error checking active shift:", err);
        if (isCurrent) setLoading(false);
      }
    };

    checkActiveShift();

    return () => {
      isCurrent = false;
    };
  }, [agentId]);

  // Update elapsed time every second when clocked in
  useEffect(() => {
    if (isClockedIn && currentShift?.clockIn) {
      elapsedIntervalRef.current = setInterval(() => {
        const clockInTime = currentShift.clockIn.toDate().getTime();
        setElapsedTime(Date.now() - clockInTime);
      }, 1000);
    }

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, [isClockedIn, currentShift]);

  // Idle timeout detection
  useEffect(() => {
    if (!isClockedIn) return;

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        handleIdleTimeout();
      }, IDLE_TIMEOUT_MS);
    };

    const handleIdleTimeout = async () => {
      console.warn("Agent idle timeout - auto clock out");
      await clockOut(true);
    };

    // Activity listeners
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, resetIdleTimer, true);
    });

    // Start initial timer
    resetIdleTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetIdleTimer, true);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [isClockedIn]);

  // Clock In
  const clockIn = useCallback(async () => {
    if (!agentId || isClockedIn) return { success: false };

    try {
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD

      const docRef = await addDoc(collection(db, "timeLogs"), {
        agentId,
        displayName: displayName || "Operations Analyst",
        clockIn: serverTimestamp(),
        clockOut: null,
        totalHours: null,
        totalPay: null,
        date: dateStr,
        payRate: PAY_RATE,
        autoClockOut: false,
        createdAt: serverTimestamp(),
      });

      setCurrentShift({
        id: docRef.id,
        agentId,
        displayName,
        clockIn: Timestamp.now(),
        clockOut: null,
        date: dateStr,
        payRate: PAY_RATE,
      });
      setIsClockedIn(true);
      setElapsedTime(0);

      return { success: true };
    } catch (err) {
      console.error("Clock in error:", err);
      return { success: false, error: err.message };
    }
  }, [agentId, displayName, isClockedIn]);

  // Clock Out
  const clockOut = useCallback(async (isAutoClockOut = false) => {
    if (!currentShift?.id) return { success: false };

    try {
      const clockOutTime = new Date();
      const clockInTime = currentShift.clockIn.toDate();
      const totalMs = clockOutTime.getTime() - clockInTime.getTime();
      const totalHours = Math.round((totalMs / (1000 * 60 * 60)) * 100) / 100; // Round to 2 decimals
      const totalPay = Math.round(totalHours * PAY_RATE * 100) / 100;

      await updateDoc(doc(db, "timeLogs", currentShift.id), {
        clockOut: serverTimestamp(),
        totalHours,
        totalPay,
        autoClockOut: isAutoClockOut,
        updatedAt: serverTimestamp(),
      });

      setCurrentShift(null);
      setIsClockedIn(false);
      setElapsedTime(0);

      return { success: true, totalHours, totalPay };
    } catch (err) {
      console.error("Clock out error:", err);
      return { success: false, error: err.message };
    }
  }, [currentShift]);

  // Format elapsed time as HH:MM:SS
  const formatElapsedTime = useCallback(() => {
    const totalSeconds = Math.floor(elapsedTime / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [elapsedTime]);

  return {
    isClockedIn,
    currentShift,
    loading,
    elapsedTime,
    formatElapsedTime,
    clockIn,
    clockOut,
    PAY_RATE,
  };
}

/**
 * Hook for admin to view all time logs
 * @param {Object} filters - Filter options (startDate, endDate, agentId, excludeAdmins, adminIds)
 * @returns {Object} Time logs and summary stats
 */
export function useTimeLogs(filters = {}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalHours: 0, totalPay: 0, agentCount: 0 });

  useEffect(() => {
    let isCurrent = true;

    const fetchLogs = async () => {
      try {
        let q = query(
          collection(db, "timeLogs"),
          orderBy("clockIn", "desc")
        );

        // Note: Firestore doesn't support complex filtering easily
        // We'll filter client-side for date ranges
        const snapshot = await getDocs(q);
        if (!isCurrent) return;

        let allLogs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Apply date filters client-side
        if (filters.startDate) {
          allLogs = allLogs.filter((log) => log.date >= filters.startDate);
        }
        if (filters.endDate) {
          allLogs = allLogs.filter((log) => log.date <= filters.endDate);
        }
        if (filters.agentId) {
          allLogs = allLogs.filter((log) => log.agentId === filters.agentId);
        }

        // Exclude admin logs if specified (admins don't track time)
        if (filters.excludeAdmins && filters.adminIds?.length > 0) {
          allLogs = allLogs.filter((log) => !filters.adminIds.includes(log.agentId));
        }

        // Calculate stats
        const completedLogs = allLogs.filter((log) => log.totalHours != null);
        const totalHours = completedLogs.reduce((sum, log) => sum + (log.totalHours || 0), 0);
        const totalPay = completedLogs.reduce((sum, log) => sum + (log.totalPay || 0), 0);
        const uniqueAgents = new Set(allLogs.map((log) => log.agentId));

        setLogs(allLogs);
        setStats({
          totalHours: Math.round(totalHours * 100) / 100,
          totalPay: Math.round(totalPay * 100) / 100,
          agentCount: uniqueAgents.size,
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching time logs:", err);
        if (isCurrent) setLoading(false);
      }
    };

    fetchLogs();

    return () => {
      isCurrent = false;
    };
  }, [filters.startDate, filters.endDate, filters.agentId, filters.excludeAdmins, filters.adminIds]);

  return { logs, loading, stats };
}

/**
 * Hook to get currently clocked-in agents (live status)
 * @returns {Object} Active agents list
 */
export function useActiveAgents() {
  const [activeAgents, setActiveAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCurrent = true;
    let unsubscribe = null;

    try {
      const q = query(
        collection(db, "timeLogs"),
        where("clockOut", "==", null)
      );

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!isCurrent) return;
          const agents = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setActiveAgents(agents);
          setLoading(false);
        },
        (err) => {
          console.error("Active agents error:", err);
          if (isCurrent) setLoading(false);
        }
      );
    } catch (err) {
      console.error("Failed to setup active agents listener:", err);
      setLoading(false);
    }

    return () => {
      isCurrent = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return { activeAgents, loading };
}

/**
 * Admin function to adjust time log
 * @param {string} logId - Time log document ID
 * @param {Object} updates - Fields to update (clockIn, clockOut)
 */
export async function adjustTimeLog(logId, updates) {
  try {
    const updateData = { ...updates, updatedAt: serverTimestamp(), adjustedByAdmin: true };

    // Recalculate hours and pay if times changed
    if (updates.clockIn && updates.clockOut) {
      const clockInTime = updates.clockIn instanceof Date ? updates.clockIn : updates.clockIn.toDate();
      const clockOutTime = updates.clockOut instanceof Date ? updates.clockOut : updates.clockOut.toDate();
      const totalMs = clockOutTime.getTime() - clockInTime.getTime();
      const totalHours = Math.round((totalMs / (1000 * 60 * 60)) * 100) / 100;
      updateData.totalHours = totalHours;
      updateData.totalPay = Math.round(totalHours * PAY_RATE * 100) / 100;
    }

    await updateDoc(doc(db, "timeLogs", logId), updateData);
    return { success: true };
  } catch (err) {
    console.error("Adjust time log error:", err);
    return { success: false, error: err.message };
  }
}

/**
 * Admin function to clock out another agent
 * @param {string} logId - Time log document ID
 * @param {Object} clockInTimestamp - The clockIn Firestore timestamp
 */
export async function adminClockOut(logId, clockInTimestamp) {
  try {
    const clockOutTime = new Date();
    const clockInTime = clockInTimestamp.toDate
      ? clockInTimestamp.toDate()
      : new Date(clockInTimestamp);
    const totalMs = clockOutTime.getTime() - clockInTime.getTime();
    const totalHours = Math.round((totalMs / (1000 * 60 * 60)) * 100) / 100;
    const totalPay = Math.round(totalHours * PAY_RATE * 100) / 100;

    await updateDoc(doc(db, "timeLogs", logId), {
      clockOut: serverTimestamp(),
      totalHours,
      totalPay,
      autoClockOut: false,
      adminClockOut: true,
      updatedAt: serverTimestamp(),
    });

    return { success: true, totalHours, totalPay };
  } catch (err) {
    console.error("Admin clock out error:", err);
    return { success: false, error: err.message };
  }
}

export default useTimeTracking;
